import React from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { Box } from "@mui/material";
import PhoneNumber from "awesome-phonenumber";
import { t } from "../../../components/translate";
import { MESSAGE_TYPE } from "./MessageCreate";

const isValidPhoneNumber = (number: string) => {
  if (number === null) return false;
  const ph = new PhoneNumber(number);
  return ph.isValid();
};

const isValidEmail = (email: string) =>
  email?.match(
    // eslint-disable-next-line no-useless-escape, max-len
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  );

export interface SMSPhoneClient {
  name: string;
  val: string;
  id?: number;
}
interface PhoneEmailMultiSelectProps {
  options: Array<SMSPhoneClient>;
  value: Array<SMSPhoneClient>;
  setValue: (v: Array<SMSPhoneClient>) => void;
  type: MESSAGE_TYPE;
  disabled?: boolean;
}

const PhoneEmailMultiSelect = ({
  options,
  value,
  setValue,
  type,
  disabled
}: PhoneEmailMultiSelectProps): React.ReactNode => {
  const [inputValue, setInputValue] = React.useState("");

  // helpers
  const setBubbleValue = () => {
    if (inputValue.length > 0) {
      const phoneWith977 = inputValue.startsWith("+977") ? inputValue : `+977${inputValue}`;
      if (type === MESSAGE_TYPE.SMS) {
        if (isValidPhoneNumber(phoneWith977)) {
          setValue([...value, { name: "", val: phoneWith977 }]);
          setInputValue("");
        }
      } else if (isValidEmail(inputValue)) {
        setValue([...value, { name: "", val: inputValue }]);
        setInputValue("");
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case ",":
      case "Enter":
      case ";":
      case " ": {
        event.preventDefault();
        event.stopPropagation();
        setBubbleValue();
        break;
      }
      default:
    }
  };

  // duplication of email/phone
  const duplicates = React.useMemo(() => {
    const countMap = new Map<string, number>();
    value.forEach((v) => {
      countMap.set(v.val, (countMap.get(v.val) || 0) + 1);
    });

    return Array.from(countMap.entries())
      .filter(([, count]) => count > 1)
      .map(([val]) => val);
  }, [value]);

  const helperText = duplicates.length
    ? `The ${type === MESSAGE_TYPE.SMS ? "phone number(s)" : "email(s)"} ${duplicates.join(
        ", "
      )} are repeating. The message will be sent only once to repeating ones.`
    : undefined;

  return (
    <Box>
      <Autocomplete
        data-testmation="selectContact"
        multiple
        freeSolo
        options={options}
        disabled={disabled}
        renderOption={(props, option) => (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <li {...props} key={option.id}>
            {option.name} ( {option.val} )
          </li>
        )}
        getOptionLabel={(option) => {
          if (typeof option === "string") {
            return option;
          }
          return `${option.name} <${option.val}>`;
        }}
        value={value}
        onChange={(_, newValue) => setValue(newValue as Array<SMSPhoneClient>)}
        inputValue={inputValue}
        onInputChange={(_, newValue) => setInputValue(newValue)}
        filterSelectedOptions
        isOptionEqualToValue={(option, v) => option.val === v.val}
        renderInput={(params) => (
          <TextField
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...params}
            variant="outlined"
            onBlur={setBubbleValue}
            onKeyDown={handleKeyDown}
            slotProps={{
              inputLabel: { shrink: true }
            }}
            margin="dense"
            label={t("campaign.msg.to")}
            placeholder={t("campaign.msg.receiver")}
            fullWidth
            helperText={helperText}
          />
        )}
      />
    </Box>
  );
};

export default PhoneEmailMultiSelect;
